import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { ItemOutCP } from '../../../models/item-out-model';
import { LineItemStatus } from '../../../enums/line-item-status.enum';
import { ReturnRequestType } from '../../../enums/return-request-type.enum';

/**
 * Minimal contract the renderer needs from its host grid component,
 * kept as an interface to avoid a circular import with ItemsOutComponent.
 */
interface ItemsOutActionsHost {
    loadServiceRequestModal(item: ItemOutCP): void;
}

@Component({
    standalone: false,
    selector: 'por-items-out-actions-menu',
    templateUrl: './items-out-actions-menu.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemsOutActionsMenuComponent implements ICellRendererAngularComp {
    private readonly cdr = inject(ChangeDetectorRef);
    private item?: ItemOutCP;
    private componentParent?: ItemsOutActionsHost;

    /** Rows that can no longer be serviced (already returned / not on rent) get no menu at all. */
    canRequestService = false;

    agInit(params: ICellRendererParams): void {
        this.item = params.data;
        this.canRequestService = ItemsOutActionsMenuComponent.isServiceable(this.item);
        this.componentParent = (params.context as { componentParent?: ItemsOutActionsHost })?.componentParent;
    }

    refresh(params: ICellRendererParams): boolean {
        this.item = params.data;
        this.canRequestService = ItemsOutActionsMenuComponent.isServiceable(this.item);
        // ag-grid drives refresh outside Angular's template bindings, so OnPush needs a nudge.
        this.cdr.markForCheck();
        return true;
    }

    private static isServiceable(item?: ItemOutCP): boolean {
        return !!item && item.lineItemStatus === LineItemStatus.Out && item.requestType !== ReturnRequestType.Return;
    }

    requestService(): void {
        if (this.item) {
            this.componentParent?.loadServiceRequestModal(this.item);
        }
    }
}
